import './AddPermissionObjectPicker.scss';

import { capitalize } from '@sonar-software/sonar-ts-lib';
import { createAsyncSelectors } from '@lib/selectors';
import { PopoverSelector } from '@ui/PopoverSelector';
import { Typography } from 'antd';

import { AccessType, coreActions, SEARCH_LIMIT } from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useAsyncState } from '@hooks';

const addPermissionObjectPickerSelectors =
	createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.search.get);

interface AddPermissionObjectPickerProps {
	onSelect: (label: string, type: AccessType, value: string) => void;
	trigger?: 'hover' | 'click';
}

type SearchOption = {
	id: number;
	label: string;
	type: AccessType;
	value: string;
};

const MIN_SEARCH_QUERY_LENGTH = 3;
const LOADING_ITEM_ID = -1;
const LOADING = 'Loading...';
const NO_RESULTS_ITEM_ID = -2;
const LOADING_VALUE = 'loading';
const NO_RESULTS_VALUE = 'no-results';

const createLoadingOption = () => ({
	id: LOADING_ITEM_ID,
	label: LOADING,
	type: '',
	value: LOADING_VALUE
});

const createNoResultsOption = () => ({
	id: NO_RESULTS_ITEM_ID,
	label: COPY_SPECIFIC.NO_RESULTS_FOUND,
	type: '',
	value: NO_RESULTS_VALUE
});

export const AddPermissionObjectPicker: React.FC<PropsWithChildren<AddPermissionObjectPickerProps>> = ({
	children,
	onSelect,
	trigger = 'click'
}) => {
	const [ query, setQuery ] = useState('');
	const { data, isRequested, isReceived } = useAsyncState(addPermissionObjectPickerSelectors);

	const getSearch = useAction(coreActions.requestGetCoreDataSecurityDataAccessSearch);
	const resetSearch = useAction(coreActions.resetGetCoreDataSecurityDataAccessSearch);

	useEffect(() => {
		if (query?.length >= MIN_SEARCH_QUERY_LENGTH) {
			getSearch({
				queryString: query,
				searchLimit: SEARCH_LIMIT
			});
		}
	}, [ getSearch, query ]);

	const handleSearch = useCallback((value: string) => {
		const cleanValue = value?.trim();
		setQuery(cleanValue);
	}, []);

	const hasNoResults = useMemo(() =>
		data && isReceived && Object.values(data).every((typeResults) =>
			typeResults.length === 0), [ data, isReceived ]);

	const searchResults = useMemo(() => {
		if (query.length < MIN_SEARCH_QUERY_LENGTH) {
			return [];
		}

		if (isRequested) {
			return [ createLoadingOption() ];
		}

		if (hasNoResults) {
			return [ createNoResultsOption() ];
		}

		if (!data) {
			return [];
		}

		const results: SearchOption[] = [];
		let idCounter = 0;
		Object.keys(data).forEach((type: string) => {
			const typeResults = data[ type ] ?? [];
			const typeCorrected = capitalize(type) as AccessType;

			if (typeCorrected !== AccessType.Object) {
				typeResults.forEach((entry) => {
					results.push({
						id: idCounter++,
						label: entry.display,
						type: typeCorrected,
						value: entry.id
					});
				});
			}
		});

		return results;
	}, [ data, query, hasNoResults, isRequested ]);

	const isSelectableOption = useCallback((value: string) =>
		value !== LOADING_VALUE && value !== NO_RESULTS_VALUE, []);

	const handleSelect = useCallback((option: SearchOption) => {
		const { label, type, value } = option;

		if (!isSelectableOption(value)) {
			return;
		}

		onSelect(label, type, value);
		setQuery('');
		resetSearch();
	}, [ onSelect, resetSearch, isSelectableOption ]);

	const handleDeselect = useCallback(() => {
		// No-op for this use case as we don't have multi-select
	}, []);

	const renderItem = useCallback((item: SearchOption) => {
		if (item.value === NO_RESULTS_VALUE) {
			return (
				<div className='add-permission-object-picker__no-results'>
					{ item.label }
				</div>
			);
		}

		return (
			<div className='add-permission-object-picker__item'>
				<Typography.Text strong>{ item.label }</Typography.Text>
				<div className='add-permission-object-picker__item-type'>
					{ item.type }
				</div>
			</div>
		);
	}, []);

	return (
		<PopoverSelector
			itemRender={ renderItem }
			listItems={ searchResults }
			onDeselect={ handleDeselect }
			onSearch={ handleSearch }
			onSelect={ handleSelect }
			placeholder={ PLACEHOLDERS_COPY.SEARCH_TO_ADD }
			singleSelect
			trigger={ trigger }>
			{ children }
		</PopoverSelector>
	);
};
